import React, { useCallback, useEffect, useState, useRef } from "react";
import { Check, Trash2, Search } from "lucide-react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../hooks/useStore";
import {
  getPartners,
  createPartner,
  updatePartner,
  deletePartner,
  clearError,
  setPartnerColor,
} from "../../store/slices/partnersSlice";
import { partnerColorsAPI } from "../../services/api";
import type { PartnerColor } from "../../constants/partnerColors";
import {
  PartnerInfoWithClientAndReg,
  Partner,
  UserFilters as Filters,
  UpdatePartnerInfoWithClientCode,
} from "../../types";
import { withVersion } from "../../utils/concurrency";
import Button from "../../components/UI/Button";
import { Modal } from "../../components/UI/Modal";
import { PartnerForm } from "../../components/partners/PartnerForm";
import { PartnerTable } from "../../components/partners/PartnerTable";
import {
  PartnerColorPicker,
  type PartnerColorUsage,
} from "../../components/partners/PartnerColorPicker";
import { Pagination } from "../../components/ListView/Pagination";
import { Input } from "../../components/UI/FormField";

export const PartnersListing: React.FC = () => {
  const { t } = useTranslation(["partners", "common"]);
  const dispatch = useAppDispatch();
  const {
    partners,
    loading,
    error,
    pagination: { total, page, pages },
  } = useAppSelector((state) => state.partner);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPartner, setEditingPartner] = useState<Partner | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  /**
   * The colour in the modal's header. Held here rather than in the form because
   * the header is the modal's, and because for an existing partner it is saved
   * the moment it is picked — not with the form's Update.
   *
   * `undefined` on a new partner until the server says which colour it would
   * get; if that never arrives the create call leaves the colour out and the
   * server assigns it anyway.
   */
  const [pickedColor, setPickedColor] = useState<PartnerColor | null>();
  const [colorUsage, setColorUsage] = useState<PartnerColorUsage[]>([]);
  const [savingColor, setSavingColor] = useState(false);

  useEffect(() => {
    if (!isModalOpen) return;
    setPickedColor(editingPartner ? (editingPartner.color ?? null) : undefined);

    let cancelled = false;
    partnerColorsAPI
      .getAll()
      .then((response) => {
        if (cancelled) return;
        const { partners: held, next } = response.data.data as {
          partners: PartnerColorUsage[];
          next: PartnerColor;
        };
        setColorUsage(held);
        if (!editingPartner) {
          setPickedColor((current) => (current === undefined ? next : current));
        }
      })
      // Without it the picker still works: no "already used" rings, and a new
      // partner's colour is left to the server.
      .catch(() => setColorUsage([]));

    return () => {
      cancelled = true;
    };
  }, [isModalOpen, editingPartner]);

  const handleColorChange = async (color: PartnerColor | null) => {
    if (!editingPartner) {
      setPickedColor(color);
      return;
    }

    const previous = pickedColor;
    setPickedColor(color);
    setSavingColor(true);
    try {
      await dispatch(
        setPartnerColor({ id: editingPartner._id, color }),
      ).unwrap();
      setColorUsage((held) => [
        ...held.filter((entry) => entry._id !== editingPartner._id),
        ...(color
          ? [
              {
                _id: editingPartner._id,
                companyName: editingPartner.companyName,
                color,
              },
            ]
          : []),
      ]);
      toast.success(t("partners:colors.updated"));
    } catch (error) {
      setPickedColor(previous);
      toast.error(
        typeof error === "string" ? error : t("partners:colors.updateFailed"),
      );
    } finally {
      setSavingColor(false);
    }
  };

  // Auto-open create modal when navigated with ?new=true (e.g. from dashboard quick action)
  useEffect(() => {
    if (searchParams.get("new") === "true") {
      setEditingPartner(null);
      setIsModalOpen(true);
      searchParams.delete("new");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  // Off = Partners view (ALL partners, incl. forwarders); On = forwarders only.
  const [forwardersOnly, setForwardersOnly] = useState(false);
  const [filters, setFilters] = useState<Filters>({
    search: "",
    page: 1,
    limit: 20,
    status: "",
  });

  const prevSearch = useRef(filters.search);

  // Build the current query params (view + filters) in one place so the list
  // effect and post-mutation refetches always stay in sync.
  const buildParams = useCallback(
    () => ({
      search: filters.search,
      page: filters.page,
      limit: filters.limit,
      status: filters.status,
      // Partners view shows everything (omit the filter); the toggle narrows to
      // forwarders only. Omitting the param → backend returns all partners.
      ...(forwardersOnly ? { forwarder: "true" } : {}),
    }),
    [filters, forwardersOnly],
  );

  // Refetch the current view — used after create/update/delete so the list never
  // shows stale rows (e.g. a record whose forwarder flag just changed).
  const refetch = useCallback(
    () => dispatch(getPartners(buildParams())),
    [dispatch, buildParams],
  );

  useEffect(() => {
    const searchChanged = prevSearch.current !== filters.search;
    prevSearch.current = filters.search;

    const params = buildParams();

    if (searchChanged) {
      if (filters.search === "") {
        dispatch(getPartners(params));
      } else {
        const t = setTimeout(() => dispatch(getPartners(params)), 750);
        return () => clearTimeout(t);
      }
      return;
    }
    dispatch(getPartners(params));
  }, [dispatch, filters, forwardersOnly, buildParams]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const handleFiltersChange = (newFilters: Partial<Filters>) => {
    setFilters((prev) => ({
      ...prev,
      ...newFilters,
      page: newFilters.page || 1,
    }));
    setSelectedIds([]);
  };

  const handlePageChange = (page: number) => {
    handleFiltersChange({ page });
  };

  const handleToggleForwarders = (next: boolean) => {
    if (next === forwardersOnly) return;
    setForwardersOnly(next);
    setSelectedIds([]);
    setFilters((prev) => ({ ...prev, page: 1 }));
  };

  // Whether a record is visible in the current view. Partners view shows all;
  // the forwarders-only view hides non-forwarders.
  const isVisibleInView = (isForwarder: boolean) =>
    !forwardersOnly || isForwarder;

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIds(partners.map((partner) => partner._id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id: string, checked: boolean) => {
    if (checked) {
      setSelectedIds((prev) => [...prev, id]);
    } else {
      setSelectedIds((prev) => prev.filter((selectedId) => selectedId !== id));
    }
  };

  const handleCreatePartner = async (data: PartnerInfoWithClientAndReg) => {
    try {
      const created = await dispatch(
        createPartner(
          pickedColor === undefined ? data : { ...data, color: pickedColor },
        ),
      ).unwrap();
      setIsModalOpen(false);

      // Partners view shows everything, so a new record is always visible there.
      // Only the forwarders-only view can hide a freshly created non-forwarder.
      if (isVisibleInView(!!created?.isForwarder)) {
        toast.success(t("partners:messages.createdSuccess"));
      } else {
        toast.info(t("partners:messages.createdHiddenInForwarders"));
      }

      refetch();
    } catch (error) {}
  };

  const handleUpdatePartner = async (data: Partial<Partner>) => {
    if (!editingPartner) return;

    try {
      if (!data.companyName || !data.address) {
        toast.error(t("partners:form.companyNameAndAddressRequired"));
        return;
      }

      const partnerInfo: UpdatePartnerInfoWithClientCode = {
        companyName: data.companyName,
        registerationCode: data.registerationCode,
        clientCode: data.clientCode,
        vatNumber: data.vatNumber,
        phoneNumber: data.phoneNumber,
        emailAddress: data.emailAddress,
        paymentTerms: data.paymentTerms,
        billingEmail: data.billingEmail,
        isForwarder: data.isForwarder,
        address: {
          street: data.address.street,
          city: data.address.city,
          state: data.address.state,
          postalCode: data.address.postalCode,
          country: data.address.country,
          // Carried through explicitly: the API requires a map location on
          // every directory write, so dropping it here fails an edit that only
          // touched an unrelated field.
          coordinates: data.address.coordinates,
        },
      };

      await dispatch(
        updatePartner({
          id: editingPartner._id,
          partnerData: withVersion(partnerInfo, editingPartner),
        }),
      ).unwrap();

      setIsModalOpen(false);
      setEditingPartner(null);

      // Partners view keeps the row regardless of the flag. The only way an edit
      // drops the row from view is unmarking a forwarder while forwarders-only.
      if (isVisibleInView(!!data.isForwarder)) {
        toast.success(t("partners:messages.updatedSuccess"));
      } else {
        toast.info(t("partners:messages.unmarkedForwarderHidden"));
      }

      refetch();
    } catch (error) {
      // The rejected reducer sets state.error (incl. the OCC conflict message),
      // which the error-toast effect surfaces — avoid double-toasting here.
      console.error(error);
    }
  };

  const handleEdit = (partner: Partner) => {
    setEditingPartner(partner);
    setIsModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm(t("partners:page.deleteConfirm"))) {
      try {
        await dispatch(deletePartner(id)).unwrap();
        toast.success(t("partners:messages.deletedSuccess"));
        setSelectedIds((prev) =>
          prev.filter((selectedId) => selectedId !== id),
        );
        refetch();
      } catch (error) {}
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingPartner(null);
  };

  const pagination = {
    page,
    pages,
    total,
    hasNext: page < pages,
    hasPrev: page > 1,
  };

  return (
    <div>
      <div className="p-6">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-dark-900 mb-4">
            {t("partners:page.title")}
          </h1>
          <div className="flex items-center justify-between gap-4 max-sm:flex-wrap">
            <div className="flex items-stretch gap-4 max-sm:flex-wrap">
              <div className="min-w-[250px] max-w-[250px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    type="text"
                    placeholder={t("partners:page.search")}
                    value={filters.search}
                    onChange={(e) =>
                      handleFiltersChange({ search: e.target.value })
                    }
                    className="pl-10 bg-white"
                  />
                </div>
              </div>

              {/* Partners (all) ⇄ Forwarders-only toggle */}
              <div className="flex items-center gap-2.5 rounded-lg border border-gray-200 bg-white px-3 select-none max-sm:h-9">
                <span
                  className={`text-sm font-medium transition-colors ${
                    forwardersOnly ? "text-gray-400" : "text-gray-900"
                  }`}
                >
                  {t("partners:page.tabPartners")}
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={forwardersOnly}
                  aria-label={t("partners:page.tabForwarders")}
                  onClick={() => handleToggleForwarders(!forwardersOnly)}
                  className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 ${
                    forwardersOnly ? "bg-primary" : "bg-gray-300"
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-sm ring-1 ring-black/5 transition-transform duration-200 ${
                      forwardersOnly ? "translate-x-4.5" : "translate-x-0.5"
                    }`}
                  />
                </button>
                <span
                  className={`text-sm font-medium transition-colors ${
                    forwardersOnly ? "text-gray-900" : "text-gray-400"
                  }`}
                >
                  {t("partners:page.tabForwarders")}
                </span>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Button onClick={() => setIsModalOpen(true)} variant="primary">
                {t("partners:page.addPartner")}
              </Button>
            </div>
          </div>
        </div>

        {selectedIds.length > 0 && (
          <div className="mb-4 flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <Check className="h-5 w-5 text-blue-600" />
              <span className="text-blue-800 font-medium">
                {t("partners:page.itemsSelected", {
                  count: selectedIds.length,
                })}
              </span>
            </div>
            <button className="inline-flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors">
              <Trash2 className="h-4 w-4" />
              <span>{t("partners:page.deleteSelected")}</span>
            </button>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <PartnerTable
            partners={partners}
            selectedIds={selectedIds}
            onSelectAll={handleSelectAll}
            onSelectOne={handleSelectOne}
            onEdit={handleEdit}
            onDelete={handleDelete}
            loading={loading}
            withColor
          />
          <Pagination
            pagination={pagination}
            onPageChange={handlePageChange}
            pageSize={filters.limit}
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto">
        <Modal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          title={
            editingPartner
              ? `${t("partners:page.idPrefix")}${editingPartner._id.slice(-6).toUpperCase()}`
              : t("partners:page.createPartner")
          }
          headerExtra={
            <PartnerColorPicker
              value={pickedColor}
              onChange={handleColorChange}
              usage={colorUsage}
              partnerId={editingPartner?._id}
              busy={savingColor}
            />
          }
          size="lg"
        >
          <div className="flex-1 overflow-y-auto">
            <PartnerForm
              partner={editingPartner || undefined}
              onSubmit={(data) => {
                if (editingPartner) {
                  return handleUpdatePartner(data as any);
                } else {
                  return handleCreatePartner(
                    data as PartnerInfoWithClientAndReg,
                  );
                }
              }}
              loading={loading}
              isEditMode={!!editingPartner}
              requireRegistrationCode={true}
              withBilling={true}
              withForwarder={true}
            />
          </div>
        </Modal>
      </div>
    </div>
  );
};
